import { useCallback, useEffect, useRef, useState } from "react"
import { API_METHODS, makeApiCall, makeCRMApiCall } from "../services/apiService"
import { IContact } from "../types/IContact"
import { Provider, ProviderSpecialty } from "../types/IProvider"
import { getCurrentUserContactId } from "./generalHooks"

type SelectedItem<T> = T extends Array<infer H> ? H : T

export interface ApiCallOptions<T> {
	crm?: boolean
	method?: API_METHODS
	body?: BodyInit
	additionalHeaders?: HeadersInit
	noHeader?: boolean
	handlerOnly?: boolean
	parseJSON?: boolean
	errorParser?: (error: any) => any
	resultParser?: (result: any) => T
	onSuccess?: (result?: T) => void
	canFetch?: boolean
	defaultSelectedItem?: SelectedItem<T>
	refetchOnChanges?: any[]
	delay?: number
	customApiPath?: string
	parseHeaders?: string[]
	customApiBaseUrl?: string
}

const defaultErrorParser = (err: any) => JSON.parse(err)
const defaultResultParser = (r: any) => JSON.parse(r)

// make sure method, body, errorParser, and resultParser are memoized to prevent refetching
export function useApiCall<T>(
	path: string,
	{
		crm,
		refetchOnChanges = [],
		method = "GET",
		body,
		canFetch = true,
		additionalHeaders,
		noHeader,
		handlerOnly,
		parseJSON,
		errorParser = parseJSON ? defaultErrorParser : undefined,
		resultParser = parseJSON ? defaultResultParser : undefined,
		onSuccess,
		defaultSelectedItem,
		delay = 0,
		customApiPath,
		customApiBaseUrl,
		parseHeaders
	}: ApiCallOptions<T> = {}
) {
	const [data, setData] = useState<T | null>(null)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<any>(undefined)
	const [success, setSuccess] = useState<boolean>(false)
	const [selected, setSelected] = useState<SelectedItem<T>>(defaultSelectedItem)
	const [debouncedPath, setDebouncedPath] = useState<string>("")
	const abortControllerRef = useRef<AbortController | null>(null)

	useEffect(() => {
		if (path === "" && typeof path !== "undefined") {
			setData(null)
			setError(null)
			setLoading(false)
			return
		}

		const handler = setTimeout(() => setDebouncedPath(typeof path === "undefined" ? "undefined" : path), delay)

		return () => {
			setDebouncedPath("")
			clearTimeout(handler)
		}
	}, [path, delay])

	const abort = () => {
		if (abortControllerRef.current) abortControllerRef.current.abort()
	}

	const fetchData = useCallback(() => {
		if (abortControllerRef.current) abortControllerRef.current.abort()
		const controller = new AbortController()
		abortControllerRef.current = controller

		const handleFetch = async () => {
			setLoading(true)
			setError("")

			try {
				const response: T = crm
					? await makeCRMApiCall(path, method, body, customApiPath, parseHeaders)
					: await makeApiCall(path, method, body, customApiPath, additionalHeaders, noHeader, controller.signal, undefined, customApiBaseUrl)
				setData(resultParser ? resultParser(response) : response)
				setSuccess(true)
				if (onSuccess) onSuccess(response)
			} catch (err) {
				if (err.name !== "AbortError") {
					console.log("ERROR", err.name, err.message)
					console.error(err)
					setError(errorParser ? errorParser(err) : err)
				}
			} finally {
				setLoading(false)
			}
		}

		if (canFetch && !!debouncedPath) handleFetch()
	}, [debouncedPath, method, body, additionalHeaders, noHeader, crm, errorParser, onSuccess, canFetch, resultParser, delay]) // eslint-disable-line

	useEffect(() => {
		if (!handlerOnly) {
			fetchData()
			return abort
		}
	}, [fetchData, handlerOnly, ...refetchOnChanges]) // eslint-disable-line

	return { data, loading, error, success, selected, fetchData, setData, setLoading, setError, setSuccess, setSelected, abort }
}

export function useContact(id?: string, options: ApiCallOptions<IContact> = {}) {
	const contactId = id || getCurrentUserContactId()
	const { data, setData, fetchData, ...state } = useApiCall<IContact>(
		`contacts(${contactId})?$expand=parentcustomerid_account($select=accountid,name,accountnumber,address1_composite,address1_city,address1_country,address1_county,address1_fax,address1_stateorprovince,address1_line1,address1_line2,address1_line3,address1_postalcode,emailaddress1,fax,industrycode,telephone1,numberofemployees)`,
		{ crm: true, ...options }
	)
	return { ...state, contact: data, setContact: setData, fetchContact: fetchData }
}

export function useRoles() {
	const { data, ...state } = useApiCall<string[]>("getRoles", { crm: true, customApiPath: "_acmgapi", parseJSON: true })
	return { ...state, roles: data }
}

export const accountExpandQuery =
	"$expand=cw_Account($select=name,address1_composite,address1_city,address1_stateorprovince,address1_line1,address1_line2,address1_line3,address1_postalcode,telephone1,fax,websiteurl)"
const providerParser = ({ value }: { value: Provider[] }): Provider[] => value
const errorParser = (error: any) => JSON.parse(error).message

export function useProvidersWithSpecialties(queryModifier?: string, options: ApiCallOptions<Provider[]> = {}) {
	const [done, setDone] = useState(false)
	const {
		data: providers,
		setData: setProviders,
		loading,
		error,
		success,
		selected,
		setSelected
	} = useApiCall<Provider[]>(`cw_practice_providers?${accountExpandQuery}${queryModifier || ""}`, { resultParser: providerParser, errorParser, crm: true, ...options })
	const { data: specialties } = useApiCall<{ id: string; specialties: ProviderSpecialty[] }[]>("/providers/specialties", { method: "POST", body: JSON.stringify(providers), canFetch: success })

	useEffect(() => {
		if (!specialties) return
		setProviders(prev => prev.map(p => ({ ...p, specialties: specialties.find(s => s.id === p.cw_practice_providerid)?.specialties })))
		setDone(true)
	}, [specialties, setProviders])

	return { providers, setProviders, loading, error, allDone: done, providersDone: success, selectedProvider: selected, setSelectedProvider: setSelected }
}
